import { BoxShadowWithRadius } from 'accessibility/focusStyles'
import { recordConsent } from 'api/enduser'
import SenderInfo from 'components/header/SenderInfo'
import Button from 'components/lib/Button'
import useResolvePrivacyPolicy from 'hooks/useResolvePrivacyPolicy'
import useThemeColors from 'hooks/useThemeColors'
import useTrackImpressionOnce from 'hooks/useTrackImpressionOnce'
import { markActiveConversationAsRead } from 'modules/conversations'
import { declinedGDPR,receiveGDPR } from 'modules/session/actions'
import React, { LegacyRef, useEffect,useLayoutEffect,useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PUBLIC_EVENTS } from 'shared/constants/api'
import { WidgetImpressionSource } from 'types/targeting'
import { warn } from 'utils'
import { createEvent } from 'utils/events'
import { sendUsageMetricEvent } from 'utils/metrics'

import './style.css'

interface GDPRProps {
  previewRef: LegacyRef<HTMLDivElement>,
  resetChatHeight: Function,
  canRetryConsent?: boolean
}

const DeclinedConsent = ({ consentMessage, canRetryConsent, onRetryConsentClicked = () => {}, copyStyle = {} }) => (
  <div className="drift-widget-greeting-content">
    <p className="drift-widget-gdpr-consent--copy" style={copyStyle}>{consentMessage}</p>
    { canRetryConsent &&
      <div className="drift-widget-reset-consent">
        <Button
          tabIndex={0}
          className="drift-gdpr-action--reset-consent-button"
          onClick={onRetryConsentClicked}
        >
          <span className="drift-gdpr-action--text">
            Reset
          </span>
        </Button>
      </div>
    }
  </div>
)

const InquireConsent = ({ message, onConsentButtonClicked, yesConsentLabel, noConsentLabel, copyStyle={}}) => (
  <>
    <div className="drift-widget-greeting-content">
      <p className="drift-widget-gdpr-consent--copy" style={copyStyle}>{message}</p>
    </div>
    <div className="drift-gdpr-actions">
      <Button
        tabIndex={0}
        className="drift-gdpr-action--allow-consent"
        onClick={onConsentButtonClicked(true)}
      >
        <span className="drift-gdpr-action--text">
          {yesConsentLabel}
        </span>
      </Button>
      <Button
        tabIndex={0}
        className="drift-gdpr-action--deny-consent"
        onClick={onConsentButtonClicked(false)}
      >
        <span className="drift-gdpr-action--text">
          {noConsentLabel}
        </span>
      </Button>
    </div>
  </>
)

const GDPR = ({ previewRef, resetChatHeight, canRetryConsent }: GDPRProps) => {
  const dispatch = useDispatch()

  const { team = [] } = useSelector(state => (
    state.embed.configuration
  ))

  const { endUser, gdpr = {} } = useSelector(state => (
    state.session
  ))

  const { declinedConsent } = gdpr

  const activePolicy = useResolvePrivacyPolicy()

  useLayoutEffect(() => {
    if (declinedConsent) {
      resetChatHeight()
    }
  }, [declinedConsent, resetChatHeight])

  useEffect(() => {
    createEvent(PUBLIC_EVENTS.GDPR_REQUESTED)
  }, [])

  const { activeColor, gdprCopyTheme } = useThemeColors()
  const botUser = useMemo(() => team.find(user => user.bot) || {}, [team])

  useTrackImpressionOnce(WidgetImpressionSource.GDPR)

  if (!activePolicy) {
    return null
  }

  const doRecordConsent = hasConsent => async () => {
    try{
      createEvent(PUBLIC_EVENTS.GDPR_CLICKED, { accepted: hasConsent, endUser: endUser.id})
      if (hasConsent) {
        await recordConsent(endUser.id, activePolicy.message)
      }

      dispatch(receiveGDPR({
        hasConsent
      }))

      if (!hasConsent) {
        dispatch(declinedGDPR(true))
        sendUsageMetricEvent('GDPR Declined', { endUserId: endUser.id })
        markActiveConversationAsRead()
      }
    } catch(err) {
      warn(err)
    }
  }

  const resetConsentDecline = () => {
    dispatch(declinedGDPR(false))
  }

  return (
    <div className="drift-widget-gdpr-consent drift-widget-greeting">
      <style>
        {`
          .drift-widget-gdpr-consent .drift-widget-reset-consent button:focus,
          .drift-widget-gdpr-consent .drift-gdpr-actions button:focus {
            ${BoxShadowWithRadius(activeColor)}
          }
        `}
      </style>
      <SenderInfo messageText={'Data Privacy Bot'} senderIds={[botUser.id]} textStyles={gdprCopyTheme}/>
      <div ref={previewRef} className="drift-widget-gdpr-body">
        { !declinedConsent &&
          <InquireConsent
            message={activePolicy.message}
            onConsentButtonClicked={doRecordConsent}
            yesConsentLabel={activePolicy.giveConsentLabel}
            noConsentLabel={activePolicy.declineConsentLabel}
            copyStyle={gdprCopyTheme} />
        }
        { declinedConsent &&
          <DeclinedConsent
            consentMessage={activePolicy.consentDeclinedMessage}
            canRetryConsent={canRetryConsent}
            onRetryConsentClicked={resetConsentDecline}
            copyStyle={gdprCopyTheme}/>
        }
      </div>
    </div>
  )
}

export default GDPR
